import { Layer, Rect, Text, Group } from 'react-konva';
import { GanttAccessRightsEnum } from '@thinknet-gmbh/platform-common';
import { PLATFORM_THEME_COLORS } from '@thinknet-gmbh/platform-common-ui';
import {
  COLUMN_WIDTH,
  COLUMN_HEIGHT,
  HEADER_ROW_HEIGHT,
  DATE_COLUMN_WIDTH,
} from 'src/pages/gantt/gantt-calendar/gantt-view/gantt-view.constants';

import {
  checkIfStartEntryInView,
  getAdditionalHeight,
  getCornerRadiusForEntry,
  getEntryColors,
  onEntryClickHandler,
  onEntryHoverHandler,
} from './data-layer.helpers';
import { IDataLayer } from './data-layer.interface';

const DataLayer = ({
  ganttControl,
  ganttData,
  moveRecordEntry,
  dateMapByDate,
  setPopoverInfo,
  headerTopOffset,
  dateBarLeftOffset,
}: IDataLayer) => {
  const { highlightProjectIds = [] } = ganttControl;
  const actionAllowed = ganttControl.access === GanttAccessRightsEnum.EDIT;

  return (
    <Layer>
      {ganttData.flatMap(({ ganttRecords = [], employee, ganttRecordsByDateMap }, employeeIndex) =>
        ganttRecords.flatMap(({ entries = [], dateString }: any) => {
          if (!dateMapByDate[dateString]) return null;
          const { index: startDateIndex = 5 } = dateMapByDate[dateString] || {};
          const columnStartX = employeeIndex * COLUMN_WIDTH + DATE_COLUMN_WIDTH;
          const columnStartY = COLUMN_HEIGHT * startDateIndex + HEADER_ROW_HEIGHT;

          return entries.flatMap((entry: any, index: number) => {
            const { groupId, type, source = {} } = entry;
            const defaultHeight = COLUMN_HEIGHT / entries.length;
            const isNeedDrawEntry = checkIfStartEntryInView(
              ganttRecordsByDateMap,
              entry,
              dateMapByDate,
              dateString
            );

            let height = defaultHeight;

            if (isNeedDrawEntry) {
              height += getAdditionalHeight(
                ganttRecordsByDateMap,
                entry,
                dateMapByDate,
                dateString
              );
            }

            const columnEndY = columnStartY + index * defaultHeight;
            const isValidSize = COLUMN_HEIGHT <= height;
            const [primaryColor, secondaryColor] = getEntryColors({ type, source });
            const isNotSearchedProject =
              highlightProjectIds.length && !highlightProjectIds.includes(source.projectId);

            return (
              isNeedDrawEntry && (
                <Group
                  opacity={isNotSearchedProject ? 0.3 : 1}
                  key={`${employee._id}-${dateString}-${groupId}`}
                  draggable={actionAllowed}
                  onClick={onEntryClickHandler({
                    entryView: {
                      x: columnStartX - dateBarLeftOffset,
                      y: columnEndY - headerTopOffset,
                      height,
                      width: COLUMN_WIDTH,
                    },
                    entry,
                    setPopoverInfo,
                  })}
                  onDragEnd={(event: any) => {
                    const { x, y } = event.target.getClientRect();
                    const pointX = x + 15 + COLUMN_WIDTH / 2;
                    const pointY = y + 15 + COLUMN_HEIGHT / 2;

                    document.body.style.cursor = 'default';
                    const validDropTarget = moveRecordEntry(
                      { pointX, pointY },
                      { sourceId: source._id, groupId }
                    );

                    if (!validDropTarget) {
                      event.currentTarget.setAttrs({ x: 0, y: 0 });
                    }
                  }}
                  {...onEntryHoverHandler(height)}
                >
                  <Rect
                    perfectDrawEnabled={false}
                    x={columnStartX + 2}
                    y={columnEndY + 1}
                    width={COLUMN_WIDTH - 4}
                    height={height - 2}
                    fill={
                      entry.source?.flexible ? PLATFORM_THEME_COLORS.tone.lightBlue : primaryColor
                    }
                    stroke={
                      entry.source?.flexible ? PLATFORM_THEME_COLORS.primary.blue : secondaryColor
                    }
                    strokeWidth={1}
                    cornerRadius={getCornerRadiusForEntry(
                      ganttRecordsByDateMap,
                      entry,
                      dateMapByDate
                    )}
                    shadowColor="#000000"
                    shadowBlur={16}
                    shadowOffset={{ x: 0, y: 2 }}
                    shadowOpacity={0}
                  />

                  <Text
                    fontFamily="Inter"
                    ellipsis
                    wrap="none"
                    rotation={270}
                    text={source.stripeName || source.name}
                    align="center"
                    verticalAlign="middle"
                    x={columnStartX}
                    padding={1}
                    y={columnEndY + (isValidSize ? height : COLUMN_HEIGHT) - 1}
                    fontSize={10}
                    width={isValidSize ? height - 3 : 0}
                    fill={PLATFORM_THEME_COLORS.primary.black}
                    height={COLUMN_WIDTH}
                    fontStyle="bold"
                  />
                </Group>
              )
            );
          });
        })
      )}
    </Layer>
  );
};
export default DataLayer;
